/**
 * VerifyFacet: one line of a settings Verify result, passed or needing
 * attention. Shared by every Verify action in Settings (the artwork public
 * bucket, the store SKUs check), so they read the same way.
 *
 * Colour is never the only signal: the glyph differs (check or cross) and the
 * screen-reader suffix says the outcome in words. That suffix comes from the
 * caller, already translated, so a zh session never hears English.
 */
import type { ReactNode } from 'react';
import { Check, X } from 'lucide-react';

export interface VerifyFacetProps {
  ok: boolean;
  label: ReactNode;
  /** Screen-reader outcome for a passed check (already translated). */
  srPassedLabel: string;
  /** Screen-reader outcome for a check that needs attention (already translated). */
  srFailedLabel: string;
  /** Optional detail under the label (a next step, a link). */
  children?: ReactNode;
}

export function VerifyFacet({ ok, label, srPassedLabel, srFailedLabel, children }: VerifyFacetProps) {
  const icon = ok ? (
    <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
  ) : (
    <X className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
  );
  // Both states use text-foreground: a failing facet is the one the operator
  // most needs to read, so it must never render quieter than a passing one.
  const text = (
    <span className="text-xs text-foreground">
      {label}
      <span className="sr-only">: {ok ? srPassedLabel : srFailedLabel}</span>
    </span>
  );
  if (!children) {
    return (
      <div className="flex items-center gap-2">
        {icon}
        {text}
      </div>
    );
  }
  // With a detail: the detail shares the label's column, under it.
  return (
    <div className="flex items-start gap-2">
      <span className="flex h-4 shrink-0 items-center">{icon}</span>
      <div className="min-w-0 space-y-0.5">
        {text}
        <div className="text-xs text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}
